import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Scene } from '@lumen5/luminary';
import { TextAlignment, VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import { NarrativeThreadEnum, RecommendationDefaults } from '../../client';
import { positionAsset } from '../positioningUtils';
import type { ToolExecutionContext } from '../types';
import { ASSET_TYPES, getTemplateFontURL, getTextAndHighlightsFromMarkdown } from '../video-state';

import type { ToolConstructorArgs } from './Tool';
import { VideoStateTool } from './VideoStateTool';

import { checkpointVideo } from 'js/src/data/video-data';
import { refreshFirstScenePreviewOperation } from 'js/src/data/video-planner';
import { intToHex, parseHexColor } from 'js/src/libs/color-utils';
import type { RootState } from 'js/src/libs/RootState';
import { splitHighlightsAcrossSubscenes } from 'js/src/libs/scene-utils';
import { saveRawLuminary } from 'js/src/luminary/index';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { calculateAndSetRecommendedSceneDurationOperation } from 'js/src/luminary/luminary-operations';
import { resizeTextToFitOnScene } from 'js/src/luminary/luminary-utils';
import {
    setTextAssetSizeOperation,
    setTextColorOperation,
} from 'js/src/luminary/text-editing-operations';

export const legacySetTextSchema = z.object({
    scene_index: z.number().describe('The scene index (0, 1, 2, etc.)'),
    asset_name: z.string().describe(`The ${ASSET_TYPES.TEXT} asset name (e.g. text_1)`),
    subscene_index: z
        .number()
        .min(0)
        .optional()
        .describe(
            'The subscene index (optional, defaults to 0). Use this to target specific subscenes when a scene has multiple subscenes.',
        ),
    text: z
        .string()
        .optional()
        .describe(
            'The text content to set. Use **text** to highlight portions of text (e.g., "Welcome to **Lumen5**")',
        ),
    font_size: z.number().optional().describe('The font size to set'),
    font_family: z.string().optional().describe('The font family to set'),
    color: z
        .string()
        .regex(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/)
        .optional()
        .describe('The text color as a hex code.'),
    position: z
        .object({
            x: z.number().describe('X position'),
            y: z.number().describe('Y position'),
        })
        .optional()
        .describe(
            'The x,y coordinates of where the text box is located on the canvas. Use this to MOVE the text box to a specific location. Do NOT use this for text alignment/justification.',
        ),
    width: z.number().min(1).optional().describe('The maximum width of the text asset in pixels'),
    height: z.number().min(1).optional().describe('The maximum height of the text asset in pixels'),
    alignment: z
        .enum(TextAlignment)
        .optional()
        .describe(
            'Text justification within the text box - how text lines up inside (left, center, right). Use this when users want to center-align, left-align, or right-align text.',
        ),
});
export type SetTextArgs = z.infer<typeof legacySetTextSchema>;

export interface SetTextConstructorArgs extends ToolConstructorArgs {
    narrativeThread?: NarrativeThreadEnum;
}

export class LegacySetTextTool extends VideoStateTool<
    typeof legacySetTextSchema,
    SetTextConstructorArgs
> {
    protected override readonly schema = legacySetTextSchema;
    protected override readonly toolName = TOOL_NAMES.setText;
    protected override readonly toolDescription = `Set text properties an asset of type ${ASSET_TYPES.TEXT} on a given scene. Do NOT use this tool on ${ASSET_TYPES.CAPTION} or ${ASSET_TYPES.FANCY_CAPTION} assets.`;

    private narrativeThread: NarrativeThreadEnum;

    public constructor(args: SetTextConstructorArgs) {
        const { narrativeThread, ...baseArgs } = args;
        super(baseArgs as ToolConstructorArgs);
        this.narrativeThread = narrativeThread ?? NarrativeThreadEnum.TEXT;
    }

    public override getToolDisplayName(): string {
        return 'Setting text';
    }

    protected override onEditApplied(args: SetTextArgs): void {
        // Editing text on the first scene changes the thumbnail.
        if (args.scene_index === 0) {
            this.dispatch(refreshFirstScenePreviewOperation());
        }
    }

    public get statusIcon(): string {
        return 'title';
    }

    public async execute(args: SetTextArgs, context: ToolExecutionContext) {
        const {
            scene_index,
            asset_name,
            subscene_index = 0,
            text: rawText,
            font_size,
            font_family,
            color,
            alignment,
            position,
            width,
            height,
        } = args;

        // LLMs sometimes emit a literal backslash-n sequence instead of a real newline
        const text = rawText?.replaceAll('\\n', '\n');

        // The agent sends color as a hex string; convert to the integer Luminary stores. Reject
        // malformed input with a corrective error before any other work.
        let colorInt: number | undefined;
        if (color !== undefined) {
            const parsed = parseHexColor(color);
            if (parsed === null) {
                return 'Error: color must be a hex string like "#ff0000"';
            }
            colorInt = parsed;
        }

        try {
            // Business logic validation (schema validation already done)
            if (
                text === undefined &&
                font_size === undefined &&
                colorInt === undefined &&
                !font_family &&
                !alignment &&
                !position &&
                width === undefined &&
                height === undefined
            ) {
                return 'Error: At least one of text, font_size, color, font_family, alignment, position, width or height must be provided';
            }

            if (scene_index >= this.luminary.scenes.length) {
                return `Error: scene_index ${scene_index} is out of bounds for the current video`;
            }

            const scene = this.luminary.scenes[scene_index];
            if (!scene) {
                return `Error: No scene found at index ${scene_index}`;
            }

            // Check if text contains subscene token (||) - requires special handling
            if (text !== undefined && text.includes(RecommendationDefaults.SUBSCENE_TOKEN)) {
                return this.handleSubsceneCreation(scene, scene_index, asset_name, text, context);
            }

            // Check if subscene_index is valid for this scene
            if (subscene_index >= scene.subscenes.length) {
                return `Error: subscene_index ${subscene_index} is out of bounds for scene ${scene_index} (has ${scene.subscenes.length} subscenes)`;
            }

            // Get asset from the appropriate subscene or scene level
            let asset;
            if (scene.subscenes.length > 1) {
                // Scene has subscenes - get asset from specific subscene
                const subscene = scene.subscenes[subscene_index];
                asset = subscene.getAssetByName(asset_name);
                if (!asset) {
                    return `Error: No asset found with name ${asset_name} in subscene ${subscene_index} of scene ${scene_index}`;
                }
            } else {
                // Scene has no subscenes - get asset from scene level (ignore subscene_index)
                asset = scene.getAssetByName(asset_name);
                if (!asset) {
                    return `Error: No asset found with name ${asset_name} in scene ${scene_index}`;
                }
            }

            if (!VideoData.isTextAsset(asset)) {
                return `Error: Asset ${asset_name} in scene ${scene_index} is not a ${ASSET_TYPES.TEXT} asset`;
            }

            if (!asset.isEditable) {
                return `Error: Asset ${asset_name} in scene ${scene_index} is not editable`;
            }

            // Only check position control if position-related properties are being modified
            const isPositionChange = position !== undefined || height !== undefined;
            if (asset.isPositionControlledByParent && isPositionChange) {
                const parentAsset = asset.getFreelyPositionedParent();
                if (parentAsset) {
                    return `Error: Asset ${asset_name} in scene ${scene_index} is position and height controlled by asset group ${parentAsset.name} and cannot be moved. You must move asset group with name ${parentAsset.name} instead.`;
                }
                return `Error: Asset ${asset_name} in scene ${scene_index} is position and height controlled by parent and cannot be moved.`;
            }

            const textAsset = asset;
            const sceneId = scene.id;
            const assetId = textAsset.id;
            const { parentActionGroup } = context;

            // Set up operations
            const { setTextColor, setTextAssetSize } = makeOperationsCallable(
                {
                    setTextColorOperation,
                    setTextAssetSizeOperation,
                },
                this.luminary,
                this.dispatch,
                () => null as unknown as RootState,
            );

            const changes: string[] = [];

            try {
                // Set text content if provided
                if (text !== undefined) {
                    // Parse text for highlights and get clean text
                    const { text: cleanText, highlights } = getTextAndHighlightsFromMarkdown(text);

                    // Check if any text asset in the scene is currently being edited
                    const isEditingText = scene
                        .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
                        .some((asset) => asset.hasTextEditingEnabled);

                    if (isEditingText) {
                        this.luminary.restoreCursorAfterEffect(() => {
                            textAsset.text = cleanText;
                        });
                    } else {
                        textAsset.text = cleanText;
                    }

                    // Always set highlights (empty array if no highlights found)
                    textAsset.textHighlights = highlights;

                    // Resize the font to fit the new text, after the new text and highlights are
                    // set. resizeTextToFitOnScene forces scene.autoUpdate on so the fit runs
                    // against up-to-date metrics — the same path the editor and recommendation
                    // flows use after a content change.
                    resizeTextToFitOnScene(scene);

                    changes.push(
                        cleanText === ''
                            ? 'text content (cleared)'
                            : `text content to ${cleanText}`,
                    );
                    if (highlights.length > 0) {
                        changes.push('highlights');
                    }
                }

                // Set font size if provided
                if (font_size !== undefined) {
                    if (typeof font_size !== 'number' || font_size <= 0) {
                        return 'Error: font_size must be a positive number';
                    }
                    setTextAssetSize(sceneId, assetId, font_size, true, false, parentActionGroup);
                    // tryToUpdateTextSize leaves the size unchanged when the requested one
                    // won't fit the box, so report the size that actually took effect rather
                    // than the one we asked for — otherwise a rejected resize looks like a success.
                    const effectiveSize = Math.round(textAsset.textSize);
                    if (effectiveSize === Math.round(font_size)) {
                        changes.push(`font size to ${font_size}`);
                    } else {
                        changes.push(
                            `font size to ${effectiveSize} (requested ${font_size}, but it didn't fit the box — resize the box or shorten the text for a larger size)`,
                        );
                    }
                }

                if (font_family !== undefined) {
                    if (typeof font_family !== 'string' || font_family.trim() === '') {
                        return 'Error: font_family must be a non-empty string';
                    }
                    const font_url = getTemplateFontURL(this.luminary, font_family);
                    if (!font_url) {
                        return `Error: font_family ${font_family} is not available in the template`;
                    }
                    textAsset.normalStyle.fontUrl = font_url;
                    changes.push(`font family to ${font_family}`);
                }

                // Set color if provided
                if (colorInt !== undefined) {
                    // Use normalStyle.fill for the main text color
                    setTextColor(
                        sceneId,
                        assetId,
                        'normalStyle.fill',
                        colorInt,
                        false,
                        parentActionGroup,
                    );
                    changes.push(`color to ${intToHex(colorInt)}`);
                }

                // Set alignment if provided
                if (alignment !== undefined) {
                    if (!['left', 'center', 'right'].includes(alignment)) {
                        return 'Error: alignment must be one of "left", "center", or "right"';
                    }
                    // Set alignment directly on the text asset
                    textAsset.textAlign = alignment;
                    changes.push(`alignment to ${alignment}`);
                }

                // Set position if provided
                if (position !== undefined) {
                    if (typeof position !== 'object' || position === null) {
                        return 'Error: position must be an object with x and y properties';
                    }
                    if (typeof position.x !== 'number' || typeof position.y !== 'number') {
                        return 'Error: position.x and position.y must be numbers';
                    }
                    positionAsset(textAsset, {
                        x: position.x,
                        y: position.y,
                    });
                    changes.push(`position to (${position.x}, ${position.y})`);
                }

                // Set width if provided
                if (width !== undefined) {
                    if (typeof width !== 'number' || width <= 0) {
                        return 'Error: width must be a positive number';
                    }
                    textAsset.maxWidth = width;
                    textAsset.hasUserChangedMaxWidth = true;
                    changes.push(`width to ${width}px`);
                }

                // Set height if provided
                if (height !== undefined) {
                    if (typeof height !== 'number' || height <= 0) {
                        return 'Error: height must be a positive number';
                    }
                    textAsset.maxHeight = height;
                    textAsset.hasUserChangedMaxHeight = true;
                    changes.push(`height to ${height}px`);
                }

                // Save changes if we made any direct modifications
                if (
                    text !== undefined ||
                    position !== undefined ||
                    width !== undefined ||
                    height !== undefined
                ) {
                    this.dispatch(
                        checkpointVideo({
                            video_data: saveRawLuminary(this.luminary),
                            systemTriggered: false,
                            parentActionGroup,
                        }),
                    );
                }

                // Changing the text changes how long it takes to read, so re-fit the
                // scene duration to the new content. The operation respects user
                // overrides and skips voice-driven scenes, so callers don't gate on it.
                if (text !== undefined) {
                    this.recomputeSceneDuration(sceneId, parentActionGroup);
                }

                this.recordSceneFill(scene_index, 'text');

                const subsceneInfo =
                    scene.subscenes.length > 1 ? ` subscene ${subscene_index}` : '';
                return `Success: Updated ${changes.join(', ')} for asset ${asset_name} in scene ${scene_index}${subsceneInfo}`;
            } catch (error) {
                const errorMessage =
                    error instanceof Error ? error.message : 'Unknown error occurred';
                return `Error updating text properties: ${errorMessage}`;
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting text: ${errorMessage}`;
        }
    }

    /**
     * Re-fit the scene's duration to its text via the shared recommended-duration
     * operation. The operation is a no-op when the duration is user-overridden or the
     * scene is voice-driven, so it's always safe to call after a text change.
     */
    private recomputeSceneDuration(sceneId: string, parentActionGroup: string | undefined): void {
        const { calculateAndSetRecommendedSceneDuration } = makeOperationsCallable(
            {
                calculateAndSetRecommendedSceneDurationOperation,
            },
            this.luminary,
            this.dispatch,
            () => this.getState(),
        );

        calculateAndSetRecommendedSceneDuration(sceneId, false, parentActionGroup);
    }

    /**
     * Handle subscene creation when text contains || (subscene token).
     * This creates multiple subscenes from a single text string, allowing
     * text to transition while media continues playing.
     */
    private handleSubsceneCreation(
        scene: Scene,
        sceneIndex: number,
        assetName: string,
        text: string,
        context: ToolExecutionContext,
    ): string {
        // Only allow subscene creation for TEXT narrative thread
        if (this.narrativeThread !== NarrativeThreadEnum.TEXT) {
            return `Error: Subscene creation with || is only supported for TEXT narrative thread videos. Current narrative thread: ${this.narrativeThread}`;
        }

        // Get the text asset from the scene
        const asset = scene.getAssetByName(assetName);
        if (!asset) {
            return `Error: No asset found with name ${assetName} in scene ${sceneIndex}`;
        }

        if (!VideoData.isTextAsset(asset)) {
            return `Error: Asset ${assetName} in scene ${sceneIndex} is not a ${ASSET_TYPES.TEXT} asset`;
        }

        if (!asset.isEditable) {
            return `Error: Asset ${assetName} in scene ${sceneIndex} is not editable`;
        }

        try {
            // Parse markdown first to extract highlights from the full text
            const { text: cleanText, highlights } = getTextAndHighlightsFromMarkdown(text);

            // Split text by subscene token
            const chunks = cleanText
                .split(RecommendationDefaults.SUBSCENE_TOKEN)
                .map((c) => c.trim());
            const numSubscenes = chunks.length;

            if (numSubscenes < 2) {
                return 'Error: Text must contain at least one || separator to create subscenes';
            }

            // Calculate how many subscenes need to be created
            // scene.subscenes[0] always exists (it's the main scene)
            const currentSubsceneCount = scene.subscenes.length;
            const subscenesToCreate = numSubscenes - currentSubsceneCount;

            // Create additional subscenes if needed
            for (let i = currentSubsceneCount; i < numSubscenes; i++) {
                scene.addSubscene(i);
            }

            // Split highlights across subscenes
            const highlightChunks = splitHighlightsAcrossSubscenes(highlights, chunks);

            // Set text on each subscene (working backwards like recommendation-utils does)
            for (let i = numSubscenes - 1; i >= 0; i--) {
                const subscene = scene.subscenes[i];
                const subsceneAsset = subscene.getAssetByName(assetName);

                if (subsceneAsset && VideoData.isTextAsset(subsceneAsset)) {
                    subsceneAsset.text = chunks[i];
                    subsceneAsset.textHighlights = highlightChunks[i];
                }
            }

            // Resize the font to fit the new text across the scene and all its subscenes
            // (getAssetsByType is deep), after all text is set. See note in execute().
            resizeTextToFitOnScene(scene);

            // Save changes
            this.dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(this.luminary),
                    systemTriggered: false,
                    parentActionGroup: context.parentActionGroup,
                }),
            );

            this.recomputeSceneDuration(scene.id, context.parentActionGroup);

            this.recordSceneFill(sceneIndex, 'text');

            const createdInfo =
                subscenesToCreate > 0 ? ` (created ${subscenesToCreate} new subscene(s))` : '';
            return `Success: Created ${numSubscenes} subscenes for asset ${assetName} in scene ${sceneIndex}${createdInfo}. Text distributed: ${chunks.map((c, i) => `[${i}] "${c}"`).join(', ')}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error creating subscenes: ${errorMessage}`;
        }
    }
}
